import { useEffect, useState, useCallback } from "react";
import { X, ImagePlus, Film, RefreshCw, Music, Upload } from "lucide-react";
import { listReelAssets, getReelAssetUrl } from "@/services/editService";

/** Media bin — a reusable library of everything uploaded to this project. Click a
 *  tile to drop it onto the timeline at the playhead (re-uses the upload, no re-upload).
 *  Also drag a tile out: it carries an `application/x-reel-asset` payload the timeline
 *  drop handler understands. */
export default function MediaPool({ open, onClose, projectId, onInsert, onUploadClick, refreshKey }) {
  const [visual, setVisual] = useState([]);
  const [audio, setAudio] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    listReelAssets(projectId)
      .then((d) => { setVisual(d.visual || []); setAudio(d.audio || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh, refreshKey]);

  if (!open) return null;
  return (
    <div className="absolute inset-y-0 right-0 z-30 w-72 bg-[#0f0e0c] border-l border-white/10 flex flex-col shadow-2xl">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-sm font-semibold text-white flex items-center gap-1.5"><ImagePlus size={14} /> Media bin</span>
        <div className="flex items-center gap-2">
          {onUploadClick && <button onClick={onUploadClick} title="Upload media" className="text-white/50 hover:text-white"><Upload size={13} /></button>}
          <button onClick={refresh} title="Refresh" className="text-white/50 hover:text-white"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /></button>
          <button onClick={onClose} title="Close" className="text-white/50 hover:text-white"><X size={15} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        <p className="text-[10px] text-white/40 px-1 pb-2">Click to add at the playhead, or drag onto the timeline. No re-upload.</p>
        <div className="grid grid-cols-2 gap-2">
          {visual.map((a) => (
            <button key={a.key} onClick={() => onInsert?.(a)} title={a.filename || a.key}
              draggable
              onDragStart={(e) => { e.dataTransfer.setData("application/x-reel-asset", JSON.stringify(a)); e.dataTransfer.effectAllowed = "copy"; }}
              className="group relative rounded-lg overflow-hidden border border-white/10 hover:border-sky-400/60 bg-black/40"
              style={{ aspectRatio: "16/9" }}>
              {a.kind === "video"
                ? <video src={getReelAssetUrl(a.key)} className="w-full h-full object-cover" muted preload="metadata" />
                : <img src={getReelAssetUrl(a.key)} alt="" className="w-full h-full object-cover" loading="lazy" />}
              <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[8px] text-white/80 px-1 py-0.5 truncate text-left">{a.filename || a.key}</span>
              {a.kind === "video" && <Film size={9} className="absolute top-1 right-1 text-violet-200" />}
            </button>
          ))}
          {!visual.length && !loading && (
            <p className="col-span-2 text-[11px] text-white/40 text-center py-6">No media yet — upload a clip with the Media button.</p>
          )}
        </div>
        {audio.length > 0 && (
          <div className="mt-3 border-t border-white/5 pt-2">
            <p className="text-[10px] text-white/40 px-1 pb-1">Audio in project</p>
            {audio.map((a) => (
              <div key={a.group_key} className="flex items-center gap-1.5 text-[10px] text-white/55 px-1 py-1">
                <Music size={10} className="text-emerald-400/70 flex-shrink-0" /> <span className="truncate">{a.filename}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
